import classNames from 'classnames';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { RuleEngine } from '../../../utils/checkoutState';
import { getAssetUrl } from '../../../utils/config';
import './style.scss';

type RuleEngineTableProps = {
  ruleEngine: RuleEngine[];
  orderId: number;
  searchTerm: string;
};

type SortingOrder = {
  indexToSort: number;
  defaultDirection: boolean;
};

type RowItem = [key: string, option: string, action: string, isMatched: string, definition: string, logLink: string];

const TABLE_HEADERS = ['group', 'option', 'action', 'isMatched', 'definition', 'logLink'];

const RuleEngineTable = ({ ruleEngine, orderId, searchTerm }: RuleEngineTableProps) => {
  const { formatMessage } = useIntl();

  const [sortingOrder, setSortingOrder] = useState<SortingOrder>({ indexToSort: 0, defaultDirection: true });

  const handleHeaderClick = (header: string) => {
    if (header === 'logLink') {
      return;
    }
    const index = TABLE_HEADERS.indexOf(header);

    setSortingOrder({
      indexToSort: index,
      defaultDirection: index === sortingOrder.indexToSort && !sortingOrder.defaultDirection
    });
  };

  const getSortedTableRows = (data: RowItem[]) =>
    sortingOrder.defaultDirection ?
      data.sort((a: RowItem, b: RowItem) => (a[sortingOrder.indexToSort] > b[sortingOrder.indexToSort] ? 1 : -1))
    : data.sort((a: RowItem, b: RowItem) => (a[sortingOrder.indexToSort] > b[sortingOrder.indexToSort] ? -1 : 1));

  const tableRows: RowItem[] = ruleEngine.map(({ key, option, action, isMatched, definition }) => [
    key,
    option,
    action,
    formatMessage({ id: `debug.ruleStatus.${isMatched ? 'matched' : 'notMatched'}` }),
    definition,
    'https://app.datadoghq.eu/logs?query=%40co.api%3Arule%20%40co.clientRequestUri%3A%22%2Frule%2Fdata%2Forder%2F' +
      orderId +
      '%22'
  ]);

  const getFormatedValue = (text: string, searchTermValue: string) => {
    if (!searchTermValue || !text) {
      return text;
    }

    const lowerText = text.toLowerCase();
    const lowerSearchTerm = searchTermValue.toLowerCase();
    const startIndex = lowerText.indexOf(lowerSearchTerm);

    if (startIndex === -1) {
      return text;
    }

    const endIndex = startIndex + searchTermValue.length;

    return (
      <>
        {text.slice(0, startIndex)}
        <mark>{text.slice(startIndex, endIndex)}</mark>
        {text.slice(endIndex)}
      </>
    );
  };

  return (
    <table className="debug__rule-engine-table">
      <thead>
        <tr>
          {TABLE_HEADERS.map(header => (
            <th key={header} onClick={() => handleHeaderClick(header)}>
              <div className="debug__container">
                <FormattedMessage id={`debug.tableHeading.${header}`} />
                {TABLE_HEADERS.indexOf(header) === sortingOrder.indexToSort && header !== 'logLink' && (
                  <img
                    className={classNames('debug__sort-icon', {
                      'debug__sort-icon--reversed': !sortingOrder.defaultDirection
                    })}
                    src={getAssetUrl('inputs/chevronDown.svg')}
                    alt="rule-to-sort"
                  />
                )}
              </div>
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {getSortedTableRows(tableRows).map(row => (
          <tr key={crypto.randomUUID()}>
            {row.map((value, valueId) =>
              valueId === 5 ?
                <td key={crypto.randomUUID()}>
                  <a href={value} target="_blank" rel="noreferrer">
                    <FormattedMessage id={'debug.tableHeading.logLink'} />
                  </a>
                </td>
              : <td key={crypto.randomUUID()}>{getFormatedValue(value, searchTerm)}</td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export default RuleEngineTable;
